import React, { useState, useEffect } from 'react';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { Modal } from './components/ui/Modal';
import { AuthModalContent } from './pages/AuthPage';
import { HomePage } from './pages/HomePage';
import { TrackPage } from './pages/TrackPage';
import { ExamPage } from './pages/ExamPage';
import { DashboardPage } from './pages/DashboardPage';
import { PricingPage } from './pages/PricingPage';
import { StudyLabPage } from './pages/StudyLabPage';
import { SchematicsCoursePage } from './pages/SchematicsCoursePage';
import { api, authStorage } from './lib/api';
import { useSeo } from './lib/useSeo';
import { normalizeSeoPath } from '../shared/seo';
import { User, Subscription, TrackSummary, ExtendedTrackType, ExamMode } from '../shared/types';

function resolveLocationPath(): string {
  const hash = window.location.hash;
  // Support legacy hash routes like #/track/electrical or #/dashboard?verified=true
  if (hash.startsWith('#/')) {
    const hashRoute = hash.slice(1).split('?')[0].split('#')[0];
    return normalizeSeoPath(hashRoute || '/');
  }
  return normalizeSeoPath(window.location.pathname || '/');
}

export function App() {
  const [currentPath, setCurrentPath] = useState<string>(() => resolveLocationPath());
  const [user, setUser] = useState<User | null>(() => authStorage.getUser());
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [showVerifiedBanner, setShowVerifiedBanner] = useState<boolean>(() => {
    return window.location.hash.includes('verified=true') || window.location.search.includes('verified=true');
  });
  const [showUpgradedBanner, setShowUpgradedBanner] = useState<boolean>(() => {
    return window.location.hash.includes('upgraded=true') || window.location.search.includes('upgraded=true');
  });
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' ? 'dark' : 'light';
  });

  // Synchronize per-route SEO metadata, canonical URL, Open Graph, and JSON-LD
  useSeo(currentPath);

  // Sync theme with HTML document class
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleToggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  // Active exam session params
  const [examParams, setExamParams] = useState<{
    track: ExtendedTrackType;
    mode: ExamMode;
    categoryId?: string;
    questionCount?: number;
  }>({ track: 'all', mode: 'study' });

  // Sync History API (popstate) and legacy hashchange navigation
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(resolveLocationPath());
    };

    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#/') && !hash.includes('auth_token=') && !hash.includes('token=')) {
        const nextPath = resolveLocationPath();
        window.history.replaceState(null, '', nextPath + window.location.search);
        setCurrentPath(nextPath);
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const navigate = (path: string) => {
    const cleanPath = normalizeSeoPath(path);
    if (window.location.pathname !== cleanPath || window.location.hash) {
      window.history.pushState(null, '', cleanPath);
    }
    setCurrentPath(cleanPath);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Verify auth session or handle OAuth callback on mount
  useEffect(() => {
    async function initAuth() {
      // Check if redirected with #auth_token=... or #token=...
      const rawHash = window.location.hash;
      let tokenFromHash: string | null = null;

      if (rawHash.includes('auth_token=')) {
        const match = rawHash.match(/auth_token=([^&]+)/);
        if (match) tokenFromHash = decodeURIComponent(match[1]);
      } else if (rawHash.includes('token=')) {
        const match = rawHash.match(/token=([^&]+)/);
        if (match) tokenFromHash = decodeURIComponent(match[1]);
      }

      // Check if redirected with ?error=...
      const searchParams = new URLSearchParams(window.location.search);
      const hashQuery = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
      const hashParams = new URLSearchParams(hashQuery);
      const urlError = searchParams.get('error') || hashParams.get('error');
      if (urlError) {
        setIsAuthModalOpen(true);
      }

      if (tokenFromHash) {
        authStorage.setToken(tokenFromHash);
        // Clear token from URL hash for security & clean URL
        window.history.replaceState(null, '', '/dashboard');
        setCurrentPath('/dashboard');
      } else if (rawHash.startsWith('#/')) {
        // Upgrade legacy hash URL to clean History API URL
        const resolved = resolveLocationPath();
        window.history.replaceState(null, '', resolved);
        setCurrentPath(resolved);
      }

      try {
        const res = await api.auth.me();
        setUser(res.user);
        setSubscription(res.subscription);
        authStorage.setUser(res.user);
      } catch (err) {
        if (authStorage.getToken()) {
          authStorage.clearToken();
        }
        setUser(null);
        setSubscription(null);
      }
    }

    initAuth();
  }, []);

  // Fetch track metadata
  useEffect(() => {
    async function loadTracks() {
      try {
        const res = await api.tracks.getAll();
        setTracks(res.tracks || []);
      } catch (err) {
        console.error('Failed to load tracks', err);
      }
    }

    loadTracks();
  }, []);

  // Handlers
  const handleStartExam = (
    track: ExtendedTrackType,
    mode: ExamMode,
    categoryId?: string,
    questionCount?: number
  ) => {
    setExamParams({ track, mode, categoryId, questionCount });
    navigate('/exam');
  };

  const handleLogout = () => {
    api.auth.logout();
    setUser(null);
    setSubscription(null);
    navigate('/');
  };

  // Determine current page view
  const renderContent = () => {
    if (currentPath.startsWith('/track/')) {
      const trackSlug = currentPath.replace('/track/', '');
      return (
        <TrackPage
          trackSlug={trackSlug}
          onNavigate={navigate}
          onStartStudy={(track, catId, count) => handleStartExam(track, 'study', catId, count)}
          onStartMock={(track, catId, count) => handleStartExam(track, 'timed_mock', catId, count)}
        />
      );
    }

    if (currentPath === '/exam') {
      return (
        <ExamPage
          track={examParams.track}
          mode={examParams.mode}
          categoryId={examParams.categoryId}
          questionCount={examParams.questionCount}
          user={user}
          onFinishExam={() => navigate('/dashboard')}
          onNavigateHome={() => navigate('/')}
          onOpenAuthModal={() => setIsAuthModalOpen(true)}
        />
      );
    }

    if (
      currentPath === '/course/schematics' ||
      currentPath === '/schematics' ||
      currentPath === '/schematics-course' ||
      currentPath === '/schematic-course'
    ) {
      return (
        <SchematicsCoursePage
          onNavigate={navigate}
          onStartExam={(track, mode, categoryId) =>
            handleStartExam(track as ExtendedTrackType, mode as ExamMode, categoryId)
          }
        />
      );
    }

    if (
      currentPath === '/study-lab' ||
      currentPath === '/flashcards' ||
      currentPath === '/formulas'
    ) {
      return (
        <StudyLabPage
          onNavigate={navigate}
          onStartExam={(track, mode) => handleStartExam(track, mode)}
        />
      );
    }

    if (currentPath === '/pricing') {
      return (
        <PricingPage
          user={user}
          subscription={subscription}
          onOpenAuthModal={() => setIsAuthModalOpen(true)}
          onNavigate={navigate}
        />
      );
    }

    if (currentPath === '/dashboard') {
      return (
        <DashboardPage
          user={user}
          subscription={subscription}
          onStartExam={(track, mode) => handleStartExam(track, mode)}
          onOpenAuthModal={() => setIsAuthModalOpen(true)}
          onNavigate={navigate}
        />
      );
    }

    // Root path: If logged in, display the personalized Candidate Dashboard
    if ((currentPath === '/' || currentPath === '') && user) {
      return (
        <DashboardPage
          user={user}
          subscription={subscription}
          onStartExam={(track, mode) => handleStartExam(track, mode)}
          onOpenAuthModal={() => setIsAuthModalOpen(true)}
          onNavigate={navigate}
        />
      );
    }

    // Unauthenticated visitor or explicit /overview: show marketing page
    return (
      <HomePage
        tracks={tracks}
        user={user}
        subscription={subscription}
        onSelectTrack={(track) => navigate(`/track/${track}`)}
        onStartStudy={(track) => handleStartExam(track, 'study')}
        onStartMock={(track) => handleStartExam(track, 'timed_mock')}
        onOpenAuthModal={() => setIsAuthModalOpen(true)}
        onNavigate={navigate}
      />
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground bg-grid-pattern selection:bg-amber-500/30 selection:text-amber-900 dark:selection:text-amber-200">
      <Header
        user={user}
        subscription={subscription}
        currentPath={currentPath}
        theme={theme}
        onToggleTheme={handleToggleTheme}
        onNavigate={navigate}
        onOpenAuthModal={() => setIsAuthModalOpen(true)}
        onLogout={handleLogout}
      />

      {showVerifiedBanner && (
        <div className="bg-emerald-500/10 border-b border-emerald-500/30 px-4 py-2.5 text-center text-xs font-medium text-emerald-700 dark:text-emerald-300 flex items-center justify-center gap-2">
          <span>✅ Your email address has been successfully verified! Your account is active.</span>
          <button
            type="button"
            onClick={() => setShowVerifiedBanner(false)}
            className="text-emerald-800 dark:text-emerald-200 hover:opacity-75 ml-2 font-bold px-1"
            aria-label="Dismiss verification notice"
          >
            ✕
          </button>
        </div>
      )}

      {showUpgradedBanner && (
        <div className="bg-amber-500/15 border-b border-amber-500/40 px-4 py-2.5 text-center text-xs font-semibold text-amber-800 dark:text-amber-300 flex items-center justify-center gap-2">
          <span>🎉 Welcome to MaintenancePrep Pro! Your 14-day free trial has been activated. Enjoy unlimited mock exams!</span>
          <button
            type="button"
            onClick={() => setShowUpgradedBanner(false)}
            className="text-amber-900 dark:text-amber-200 hover:opacity-75 ml-2 font-bold px-1"
            aria-label="Dismiss upgrade notice"
          >
            ✕
          </button>
        </div>
      )}

      <main className="flex-1">
        {renderContent()}
      </main>

      <Footer onNavigate={navigate} />

      {/* Authentication Modal */}
      <Modal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        title="MaintenancePrep Authentication"
        description="Sign in to save your mock exam history and test analytics."
        maxWidth="md"
      >
        <AuthModalContent
          onClose={() => setIsAuthModalOpen(false)}
          onSuccess={(u, s) => {
            setUser(u);
            setSubscription(s);
          }}
        />
      </Modal>
    </div>
  );
}

export default App;
